const SORT_FIELD_MAP = {
    fullName: 'fullName',
    name: 'fullName',
    email: 'email',
    phone: 'phone',
    mobile: 'phone',
    created: 'created',
    updated: 'updated',
    userStatus: 'userStatus',
    status: 'userStatus',
    lastActive: 'lastActive',
};

export const DEFAULT_LIST_STATE = {
    page: 1,
    search: '',
    type: 0,
    sortBy: 'created',
    sortOrder: 'desc',
    status: '',
    company: '',
    createdFrom: '',
    createdTo: '',
    updatedFrom: '',
    updatedTo: '',
    verifiedUser: '',
    verifiedEmail: '',
};

export const parseUserListQuery = (searchString) => {
    const params = new URLSearchParams(searchString || '');
    const page = parseInt(params.get('page') || '1', 10);
    const type = parseInt(params.get('type') || '0', 10);
    const sortBy = params.get('sort') || params.get('sortBy') || DEFAULT_LIST_STATE.sortBy;
    const sortOrder = params.get('order') || params.get('sortOrder') || DEFAULT_LIST_STATE.sortOrder;

    return {
        page: Number.isNaN(page) || page < 1 ? 1 : page,
        search: params.get('search') || '',
        type: type === 1 ? 1 : 0,
        sortBy,
        sortOrder: sortOrder === 'asc' || sortOrder === 'desc' ? sortOrder : DEFAULT_LIST_STATE.sortOrder,
        status: params.get('status') || '',
        company: params.get('company') || '',
        createdFrom: params.get('createdFrom') || '',
        createdTo: params.get('createdTo') || '',
        updatedFrom: params.get('updatedFrom') || '',
        updatedTo: params.get('updatedTo') || '',
        verifiedUser: params.get('verifiedUser') || '',
        verifiedEmail: params.get('verifiedEmail') || '',
    };
};

export const buildUserListApiQuery = (state) => {
    const page = state.page || 1;
    const type = state.type || 0;
    const search = (state.search && state.search.trim()) || '';
    const sortBy = state.sortBy || DEFAULT_LIST_STATE.sortBy;
    const sortOrder = state.sortOrder || DEFAULT_LIST_STATE.sortOrder;
    const mappedSort = SORT_FIELD_MAP[sortBy] || sortBy;

    // Match legacy working format first so older APIs keep returning data
    let filterBy = '';
    if (mappedSort && !(mappedSort === 'created' && sortOrder === 'desc')) {
        filterBy = mappedSort;
    }

    const query = new URLSearchParams();
    query.set('page', String(page));
    query.set('limit', '10');
    query.set('search', search);
    query.set('type', String(type));
    query.set('filterBy', filterBy);

    // Newer backends also read these
    if (mappedSort) {
        query.set('sortBy', mappedSort);
        query.set('sortOrder', sortOrder);
    }
    if (state.status) {
        query.set('status', state.status);
    }
    if (state.company) {
        query.set('company', state.company.trim());
    }
    if (state.createdFrom) {
        query.set('createdFrom', state.createdFrom);
    }
    if (state.createdTo) {
        query.set('createdTo', state.createdTo);
    }
    if (state.updatedFrom) {
        query.set('updatedFrom', state.updatedFrom);
    }
    if (state.updatedTo) {
        query.set('updatedTo', state.updatedTo);
    }
    if (state.verifiedUser) {
        query.set('verifiedUser', state.verifiedUser);
    }
    if (state.verifiedEmail) {
        query.set('verifiedEmail', state.verifiedEmail);
    }

    return `?${query.toString()}`;
};

export const buildUserListUrlSearch = (state) => {
    const query = new URLSearchParams();
    if (state.page && state.page !== 1) {
        query.set('page', String(state.page));
    }
    if (state.search) {
        query.set('search', state.search.trim());
    }
    if (state.type === 1) {
        query.set('type', '1');
    }
    // Always keep sort in URL so the arrow stays visible after refresh
    if (state.sortBy && state.sortOrder) {
        query.set('sort', state.sortBy);
        query.set('order', state.sortOrder);
    } else {
        query.set('sort', DEFAULT_LIST_STATE.sortBy);
        query.set('order', DEFAULT_LIST_STATE.sortOrder);
    }
    if (state.status) {
        query.set('status', state.status);
    }
    if (state.company) {
        query.set('company', state.company.trim());
    }
    if (state.createdFrom) {
        query.set('createdFrom', state.createdFrom);
    }
    if (state.createdTo) {
        query.set('createdTo', state.createdTo);
    }
    if (state.updatedFrom) {
        query.set('updatedFrom', state.updatedFrom);
    }
    if (state.updatedTo) {
        query.set('updatedTo', state.updatedTo);
    }
    if (state.verifiedUser) {
        query.set('verifiedUser', state.verifiedUser);
    }
    if (state.verifiedEmail) {
        query.set('verifiedEmail', state.verifiedEmail);
    }

    const value = query.toString();
    return value ? `?${value}` : '';
};

export const getNextSortState = (currentSortBy, currentSortOrder, columnKey) => {
    if (currentSortBy !== columnKey) {
        return { sortBy: columnKey, sortOrder: 'asc' };
    }
    if (currentSortOrder === 'asc') {
        return { sortBy: columnKey, sortOrder: 'desc' };
    }
    // Third click returns to default Created ↓
    return {
        sortBy: DEFAULT_LIST_STATE.sortBy,
        sortOrder: DEFAULT_LIST_STATE.sortOrder,
    };
};

export const getSortIndicator = (sortBy, sortOrder, columnKey) => {
    const activeSortBy = sortBy || DEFAULT_LIST_STATE.sortBy;
    const activeSortOrder = sortOrder || DEFAULT_LIST_STATE.sortOrder;

    if (activeSortBy !== columnKey) {
        return ' ↕';
    }
    return activeSortOrder === 'asc' ? ' ↑' : ' ↓';
};

export const areListStatesEqual = (a, b) => {
    if (!a || !b) {
        return false;
    }
    return JSON.stringify(a) === JSON.stringify(b);
};

export const countActiveFilters = (state) => {
    let count = 0;
    if (state.status) count += 1;
    if (state.company) count += 1;
    if (state.createdFrom || state.createdTo) count += 1;
    if (state.updatedFrom || state.updatedTo) count += 1;
    if (state.verifiedUser) count += 1;
    if (state.verifiedEmail) count += 1;
    return count;
};

export const getActiveFilterChips = (state) => {
    const chips = [];
    if (state.status) {
        chips.push({ key: 'status', label: `Status: ${state.status}` });
    }
    if (state.company) {
        chips.push({ key: 'company', label: `Company: ${state.company}` });
    }
    if (state.createdFrom || state.createdTo) {
        chips.push({
            key: 'createdRange',
            label: `Created: ${state.createdFrom || '...'} - ${state.createdTo || '...'}`,
        });
    }
    if (state.updatedFrom || state.updatedTo) {
        chips.push({
            key: 'updatedRange',
            label: `Updated: ${state.updatedFrom || '...'} - ${state.updatedTo || '...'}`,
        });
    }
    if (state.verifiedUser) {
        chips.push({ key: 'verifiedUser', label: `User verified: ${state.verifiedUser}` });
    }
    if (state.verifiedEmail) {
        chips.push({ key: 'verifiedEmail', label: `Email verified: ${state.verifiedEmail}` });
    }
    return chips;
};
